// @givmo/design-system — the public Profile page's composition, for rendering (Profile
// Enrichment plan §1.2 / §1.5).
//
// ONE implementation of the rules every public renderer applies to the served blocks — the
// organization console's "View as public" tab and the donate page's public render draw the same
// list from it (D-PE-17 (6): the preview shows a page EXACTLY as the public surface does, so the
// composition can never be two copies):
//
//   * blocks stay in OWNER ORDER — the served list IS the order (H1: kind-grouping is not a layout
//     rule);
//   * consecutive `link` rows coalesce into ONE list, a non-https link never renders;
//   * a video carries its stored poster (PE-BE9) and the provider it plays from; an embed URL that
//     is not one of the two canonical forms is withheld (the facade shows a placeholder, never a
//     frame on an author-supplied host);
//   * an image without a readable URL is kept as a placeholder rather than a broken frame;
//   * a kind this build does not know renders NOTHING and the host is told once per block — the
//     tolerant enum, so a later kind is byte-additive on every client.
//
// The input is typed STRUCTURALLY (the wire block's shape, not the generated client's type) so this
// package stays free of the api-client: `PublicProfileBlockOut` satisfies it as-is.

import { isCanonicalVideoEmbedUrl, videoProviderLabel } from '../video-embed';

/** The shape of one served block — `PublicProfileBlockOut` satisfies it structurally. */
export interface PublicProfileBlockLike {
  id: string;
  kind: string;
  title?: string | null;
  body?: string | null;
  media_url?: string | null;
  poster_url?: string | null;
}

export type RenderItem =
  | { kind: 'story'; key: string; title: string | null; body: string }
  | { kind: 'image'; key: string; src: string | null; alt: string }
  | {
      kind: 'video';
      key: string;
      embedUrl: string | null;
      /** The stored poster (PE-BE9), or null ⇒ the neutral facade naming the provider. */
      posterUrl: string | null;
      provider: 'YouTube' | 'Vimeo' | null;
      title: string | null;
    }
  | {
      kind: 'links';
      key: string;
      links: { key: string; label: string; href: string; host: string }[];
    };

export interface ComposeOptions {
  /** Told once per block of a kind this client does not know — the host's telemetry hook (§1.2). */
  onUnknownKind?: (kind: string) => void;
}

/**
 * The block's poster, read TOLERANTLY: `poster_url` is additive on the block DTOs (PE-BE9), so it is
 * read as an optional string off the wire object — absent, null or blank all mean "no poster".
 */
export function readPosterUrl(block: object): string | null {
  const raw = (block as { poster_url?: unknown }).poster_url;
  return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
}

/**
 * The public blocks in owner order, consecutive link rows coalesced into ONE list (plan §1.2), an
 * unknown kind dropped (it renders nothing — the tolerant enum — and the host is told), an image
 * without a readable URL kept as a placeholder rather than a broken frame.
 */
export function composeForRender(
  blocks: readonly PublicProfileBlockLike[],
  options: ComposeOptions = {},
): RenderItem[] {
  const items: RenderItem[] = [];
  for (const block of blocks) {
    switch (block.kind) {
      case 'story':
        items.push({
          kind: 'story',
          key: block.id,
          title: block.title ?? null,
          body: block.body ?? '',
        });
        break;
      case 'image':
        items.push({
          kind: 'image',
          key: block.id,
          src: block.media_url ?? null,
          alt: block.title ?? '',
        });
        break;
      case 'video':
        items.push({
          kind: 'video',
          key: block.id,
          embedUrl: isCanonicalVideoEmbedUrl(block.media_url) ? (block.media_url ?? null) : null,
          posterUrl: readPosterUrl(block),
          provider: videoProviderLabel(block.media_url),
          title: block.title?.trim() ? block.title.trim() : null,
        });
        break;
      case 'link': {
        const href = block.media_url ?? '';
        if (!href.startsWith('https://')) break;
        const link = { key: block.id, label: block.title ?? href, href, host: hostOf(href) };
        const last = items[items.length - 1];
        if (last !== undefined && last.kind === 'links') last.links.push(link);
        else items.push({ kind: 'links', key: block.id, links: [link] });
        break;
      }
      default:
        options.onUnknownKind?.(String(block.kind));
        break;
    }
  }
  return items;
}

function hostOf(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}
